import React from 'react';
import { Target, Eye, Award, Users, GraduationCap, Heart, CheckCircle } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Header Section */}
      <section className="gradient-bg text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">About Us</h1>
          <p className="text-xl hindi-text">हमारे बारे में</p>
        </div>
      </section>

      {/* Introduction */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-800 mb-6">
                Who We Are
              </h2>
              <p className="text-xl text-gray-600 mb-4 hindi-text">
                हम कौन हैं
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>R.P. Educational & Welfare Trust</strong> is a NITI AAYOG registered charitable organization 
                dedicated to empowering rural Bihar through education, skill development, and women empowerment initiatives.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Since our establishment in <strong>2022</strong>, we have been working tirelessly to bridge the educational 
                gap in rural communities, providing career counseling, skill training, and awareness programs to thousands 
                of students and families.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our mission is to create a society where every individual, regardless of their background, has access 
                to quality education and opportunities for growth.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="/images/gallery1.jpeg"
                alt="Our Work"
                className="rounded-lg shadow-lg w-full h-48 object-cover"
              />
              <img
                src="/images/gallery2.jpeg"
                alt="Community"
                className="rounded-lg shadow-lg w-full h-48 object-cover mt-8"
              />
              <img
                src="/images/gallery3.jpeg"
                alt="Students"
                className="rounded-lg shadow-lg w-full h-48 object-cover"
              />
              <img
                src="/images/gallery4.jpeg"
                alt="Programs"
                className="rounded-lg shadow-lg w-full h-48 object-cover mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-gradient-to-r from-purple-50 via-pink-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Mission */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-6">
                <Target className="text-purple-700 mr-4" size={48} />
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">Our Mission</h3>
                  <p className="text-gray-600 hindi-text">हमारा मिशन</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                To empower individuals in rural Bihar through quality education, career guidance, 
                and skill development programs. We strive to create equal opportunities for all, 
                focusing especially on women empowerment and youth development.
              </p>
            </div>

            {/* Vision */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-6">
                <Eye className="text-purple-700 mr-4" size={48} />
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">Our Vision</h3>
                  <p className="text-gray-600 hindi-text">हमारा विज़न</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed">
                To build a society where education and opportunity are accessible to everyone, 
                creating a future where no child is left behind and every individual can achieve 
                their full potential through knowledge and skills.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">What We Do</h2>
            <p className="text-xl text-gray-600 hindi-text">हम क्या करते हैं</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <GraduationCap size={40} />,
                title: 'Career Counseling',
                hindi: 'करियर परामर्श',
                description: 'Providing personalized career guidance and counseling to students to help them make informed decisions about their future.',
              },
              {
                icon: <Users size={40} />,
                title: 'Skill Development',
                hindi: 'कौशल विकास',
                description: 'Conducting workshops and training programs to enhance practical skills and improve employability.',
              },
              {
                icon: <Heart size={40} />,
                title: 'Women Empowerment',
                hindi: 'महिला सशक्तिकरण',
                description: 'Special programs focused on empowering women through education, skill training, and awareness.',
              },
              {
                icon: <Award size={40} />,
                title: 'Educational Support',
                hindi: 'शैक्षिक सहायता',
                description: 'Providing study materials, scholarships, and educational resources to underprivileged students.',
              },
              {
                icon: <Users size={40} />,
                title: 'Community Awareness',
                hindi: 'सामुदायिक जागरूकता',
                description: 'Organizing awareness programs on education, health, and social issues in rural communities.',
              },
              {
                icon: <GraduationCap size={40} />,
                title: 'Student Mentorship',
                hindi: 'छात्र मार्गदर्शन',
                description: 'Connecting students with mentors who guide them through their academic and career journey.',
              },
            ].map((service, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <div className="text-purple-700 mb-4">{service.icon}</div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{service.title}</h3>
                <p className="text-gray-600 hindi-text mb-3">{service.hindi}</p>
                <p className="text-gray-600 text-sm leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Registration & Certifications */}
      <section className="section-padding bg-gradient-to-r from-purple-700 to-pink-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Registration & Certifications</h2>
            <p className="text-xl hindi-text">हमारा पंजीकरण और प्रमाणपत्र</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'NITI AAYOG Registered',
                description: 'Officially registered with NITI AAYOG, Government of India',
                id: 'BR2502044736',
              },
              {
                title: '80G Certified',
                description: 'Donors get tax benefits under Section 80G of Income Tax Act',
                id: 'Tax Deduction',
              },
              {
                title: '12A Registered',
                description: 'Registered under Section 12A ensuring transparency and accountability',
                id: 'Trust Deed',
              },
            ].map((cert, index) => (
              <div key={index} className="bg-white bg-opacity-10 backdrop-blur-lg p-6 rounded-xl">
                <Award className="mb-4" size={48} />
                <h3 className="text-xl font-bold mb-2">{cert.title}</h3>
                <p className="mb-3 text-sm">{cert.description}</p>
                <p className="text-sm font-semibold bg-white bg-opacity-20 px-3 py-1 rounded inline-block">
                  {cert.id}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Services in Detail */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Our Services in Detail</h2>
            <p className="text-xl text-gray-600 hindi-text">हमारी सेवाएं विस्तार से</p>
          </div>

          <div className="space-y-6">
            {[
              {
                title: 'Career Guidance and Counseling',
                hindi: 'करियर मार्गदर्शन और परामर्श',
              },
              {
                title: 'Women Empowerment Programs',
                hindi: 'महिला सशक्तिकरण कार्यक्रम',
              },
              {
                title: 'Skill Development Workshops',
                hindi: 'कौशल विकास कार्यशालाएं',
              },
              {
                title: 'Educational Awareness Campaigns',
                hindi: 'शैक्षिक जागरूकता अभियान',
              },
              {
                title: 'Student Support and Scholarships',
                hindi: 'छात्र सहायता और छात्रवृत्ति',
              },
              {
                title: 'Community Development Initiatives',
                hindi: 'सामुदायिक विकास पहल',
              },
            ].map((item, index) => (
              <div key={`service-${item.title}-${index}`} className="bg-white p-6 rounded-lg shadow-md flex items-start">
                <CheckCircle className="text-green-500 mr-4 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h4 className="text-lg font-semibold text-gray-800">{item.title}</h4>
                  <p className="text-gray-600 hindi-text">{item.hindi}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
